import { Fragment, useEffect, useState } from 'react'

import { useQuery, useReactiveVar } from '@apollo/client'
import classNames from 'classnames'

import { isString } from '@local/utils'

import Advertisement, { AdPage, AdType } from '@web/components/Advertisement/Advertisement'
import { getADElementIdMap } from '@web/components/Advertisement/utils'
import ViewGridImageLoader from '@web/components/Loader/ViewGridImageLoader/ViewGridImageLoader'
import Loading from '@web/components/Loading'
import NoContent from '@web/components/NoContent'
import { NoContentTypes } from '@web/components/NoContent/type'
import { gql } from '@web/gql'
import { ReviewGroupExposeStatus, ReviewGroupsMediaFilter, UserReviewGroupsInput } from '@web/gql/graphql'
import useInfiniteScroll from '@web/hooks/useInfiniteScroll'
import usePageInfo from '@web/hooks/usePageInfo'
import { extractByTypename } from '@web/libs/apollo/typeGuard'
import { neloLogger } from '@web/libs/utils'
import { useSetReviewTabFilterOnInit } from '@web/pages/[objectId]/review/components/ReviewTab/ReviewTab.hook'

import { cleanupReviewTabFilter, reviewTabFilterStateVar, reviewTabPageOwnerIdVar } from './filter.state'
import { ReviewTabRowCount } from '../../index.page'
import Filters from './Filters/Filters'
import ReviewGroupCard from './ReviewGroupCard/ReviewGroupCard'
import styles from './ReviewTab.module.scss'

const userReviewGroupsQuery = gql(`
  query UserReviewGroups($input: UserReviewGroupsInput!, $connection: UserReviewGroupsConnectionInput!) {
    userReviewGroups(input: $input, connection: $connection) {
      ... on UserReviewGroupsConnection {
        edges {
          id
          ...ReviewTab_ReviewGroupCard_ReviewGroup
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`)

export const userReviewCategoriesQuery = gql(`
  query UserReviewCategories($input: UserReviewGroupCategoriesInput!) {
    userReviewGroupCategories(input: $input) {
      ...Category_ReviewGroupCategory
    }
  }
`)

// 2칸/3칸 뷰 모두 지원하기 위해서 2와 3의 공배수인 18로 설정
export const REVIEWGROUPS_PAGE_SIZE = 18

type Props = {
  rowCount: ReviewTabRowCount
}

const ReviewTab = ({ rowCount }: Props) => {
  // Query String과 Reactive Var를 통해 초기 필터를 설정하기에 최상단에 위치시켜야함
  useSetReviewTabFilterOnInit()

  const { pageOwner, isMyPage } = usePageInfo()

  const sort = useReactiveVar(reviewTabFilterStateVar.sort)
  const placeCategoryId = useReactiveVar(reviewTabFilterStateVar.placeCategoryId)
  const regionCode = useReactiveVar(reviewTabFilterStateVar.regionCode)

  const defaultMediaFilter = !!pageOwner?.stat.imageReviewCount
    ? ReviewGroupsMediaFilter.WithMedia
    : ReviewGroupsMediaFilter.All

  const [mediaFilter, setMediaFilter] = useState<ReviewGroupsMediaFilter>(
    reviewTabFilterStateVar.mediaFilter() ?? defaultMediaFilter,
  )

  useEffect(() => {
    const savedPageOwnerId = reviewTabPageOwnerIdVar()
    const currentPageOwnerId = pageOwner?.id
    if (savedPageOwnerId && currentPageOwnerId !== savedPageOwnerId) cleanupReviewTabFilter()

    if (isString(currentPageOwnerId)) reviewTabPageOwnerIdVar(currentPageOwnerId)
  }, [pageOwner?.id])

  const input: UserReviewGroupsInput = {
    sort,
    mediaFilter,
    userIdno: pageOwner?.idno!,
    placeCategoryId,
    regionCode,
  }

  const { data, loading, refetch, fetchMore } = useQuery(userReviewGroupsQuery, {
    variables: {
      input,
      connection: {
        first: REVIEWGROUPS_PAGE_SIZE,
      },
    },
    errorPolicy: 'all',
    skip: !pageOwner?.idno,
    onError: error => {
      neloLogger('error', `리뷰탭 유저 리뷰그룹목록 오류 발생: [${pageOwner?.idno}] ${error}`)
    },
  })

  const { data: categoryData } = useQuery(userReviewCategoriesQuery, {
    variables: {
      input: {
        userIdno: pageOwner?.idno!,
        mediaFilter,
      },
    },
    skip: !pageOwner?.idno,
  })

  const { UserReviewGroupsConnection, UnknownError } = extractByTypename(data?.userReviewGroups)
  const reviewCategories = categoryData?.userReviewGroupCategories ?? []

  const FetchMoreTrigger = useInfiniteScroll({
    checkLastElement: () => !UserReviewGroupsConnection?.pageInfo.hasNextPage,
    fetchMore: () =>
      fetchMore({
        variables: {
          input,
          connection: { after: UserReviewGroupsConnection?.pageInfo.endCursor, first: REVIEWGROUPS_PAGE_SIZE },
        },
      }),
  })

  const reviewGroups =
    UserReviewGroupsConnection?.edges?.filter(reviewGroup => {
      if (!isMyPage) {
        return reviewGroup.exposeStatus === ReviewGroupExposeStatus.Expose
      }
      return true
    }) || []

  const adSlotElementIdMap = getADElementIdMap(reviewGroups.length, 'REVIEW_TAB', rowCount)

  if (loading) {
    return (
      <>
        <Filters mediaFilter={mediaFilter} setMediaFilter={setMediaFilter} categories={reviewCategories} />
        <ViewGridImageLoader />
      </>
    )
  }

  const noReviewTabType = isMyPage
    ? NoContentTypes.NO_REVIEW_IN_REVIEW_TAB_TYPE_MINE
    : NoContentTypes.NO_REVIEW_IN_REVIEW_TAB

  const isMediaFilter = mediaFilter === ReviewGroupsMediaFilter.WithMedia

  if (UnknownError) {
    return <NoContent type={NoContentTypes.ERROR} isFull clickHandler={refetch} />
  }

  if (!data || !UserReviewGroupsConnection) {
    return (
      <div className={styles.review_area}>
        <h2 className="place_section_header">
          <div className={styles.place_section_header_title}>
            리뷰
            <em className={styles.count}>
              0<span className="place_blind">개</span>
            </em>
          </div>
        </h2>
        <NoContent type={noReviewTabType} />
      </div>
    )
  }

  if (!reviewGroups.length) {
    const noContentType = isMediaFilter ? NoContentTypes.REVIEW_NO_PHOTO : noReviewTabType

    return (
      <>
        <div className={styles.review_area}>
          <h2 className="place_section_header">
            <div className={styles.place_section_header_title}>
              리뷰
              <em className={styles.count}>
                0<span className="place_blind">개</span>
              </em>
            </div>
          </h2>
          {isMediaFilter && (
            <Filters mediaFilter={mediaFilter} setMediaFilter={setMediaFilter} categories={reviewCategories} />
          )}
          <NoContent type={noContentType} />
        </div>
      </>
    )
  }

  return (
    <>
      <Filters mediaFilter={mediaFilter} setMediaFilter={setMediaFilter} categories={reviewCategories} />
      <ul className={styles.review_list}>
        {reviewGroups.map((reviewGroup, index) => {
          const showAd = !!adSlotElementIdMap[index]
          return (
            <Fragment key={reviewGroup.id}>
              {/* TODO: 리뷰 탭 > 리뷰 이미지 목록 - 게시중단 케이스 추가 필요 */}
              <ReviewGroupCard reviewGroup={reviewGroup} filter={input} />
              {showAd && (
                <li className={classNames(styles.list_item, styles.type_ad)}>
                  <Advertisement
                    adSlotElementId={adSlotElementIdMap[index]}
                    adSlotElementIds={Object.values(adSlotElementIdMap)}
                    type={AdType.FEED_REVIEW_TAB}
                    page={isMyPage ? AdPage.REVIEW_TAB_MINE : AdPage.REVIEW_TAB_OTHERS}
                    userIdno={pageOwner?.idno}
                  />
                </li>
              )}
            </Fragment>
          )
        })}
      </ul>
      <FetchMoreTrigger />
      {UserReviewGroupsConnection.pageInfo.hasNextPage && <Loading height={250} />}
    </>
  )
}

export default ReviewTab
